package com.shadowings.gero.di

import com.shadowings.gero.data.datasource.JsonChapterDataSource
import com.shadowings.gero.data.datasource.LessonContentDataSource
import com.shadowings.gero.data.datasource.defaultChapters
import com.shadowings.gero.data.repository.AnalyticsRepositoryImpl
import com.shadowings.gero.data.repository.AuthRepositoryImpl
import com.shadowings.gero.data.repository.ChapterRepositoryImpl
import com.shadowings.gero.data.repository.CloudSyncRepositoryImpl
import com.shadowings.gero.data.repository.DatabaseRepositoryImpl
import com.shadowings.gero.data.repository.SentenceRepository
import com.shadowings.gero.data.repository.UserDataRepositoryImpl
import com.shadowings.gero.domain.reminder.QuizReminderScheduler
import com.shadowings.gero.domain.reminder.createQuizReminderScheduler
import com.shadowings.gero.domain.repository.AnalyticsRepository
import com.shadowings.gero.domain.repository.AuthRepository
import com.shadowings.gero.domain.repository.ChapterRepository
import com.shadowings.gero.domain.repository.CloudSyncRepository
import com.shadowings.gero.domain.repository.DatabaseRepository
import com.shadowings.gero.domain.repository.LessonContentRepository
import com.shadowings.gero.domain.repository.UserDataRepository
import com.shadowings.gero.domain.repository.provideDataStore
import com.shadowings.gero.domain.usecase.CheckDailyBonusUseCase
import com.shadowings.gero.domain.usecase.CheckStreakRestorationBackfillUseCase
import com.shadowings.gero.domain.usecase.RemoveHeartUseCase
import com.shadowings.gero.domain.usecase.ScheduleQuizReminderUseCase
import com.shadowings.gero.presentation.viewmodel.AppViewModel
import com.shadowings.gero.presentation.viewmodel.AuthViewModel
import com.shadowings.gero.presentation.viewmodel.ExplanationViewModel
import com.shadowings.gero.presentation.viewmodel.FeaturePollViewModel
import com.shadowings.gero.presentation.viewmodel.IntroViewModel
import com.shadowings.gero.presentation.viewmodel.LandingViewModel
import com.shadowings.gero.presentation.viewmodel.ProfileViewModel
import com.shadowings.gero.presentation.viewmodel.QuizViewModel
import com.shadowings.gero.presentation.viewmodel.RuleDetailViewModel
import org.koin.core.module.dsl.viewModel
import org.koin.dsl.module

val appModule = module {
    single { JsonChapterDataSource(defaultChapters) }
    single<ChapterRepository> { ChapterRepositoryImpl(dataSource = get()) }
    single<LessonContentRepository> { LessonContentDataSource() }
    single { provideDataStore() }
    single<UserDataRepository> { UserDataRepositoryImpl(dataStore = get()) }
    single<DatabaseRepository> { DatabaseRepositoryImpl() }
    single<AuthRepository> { AuthRepositoryImpl() }
    single<AnalyticsRepository> { AnalyticsRepositoryImpl() }
    single<CloudSyncRepository> {
        CloudSyncRepositoryImpl(
            authRepository = get(),
            userDataRepository = get()
        ).apply { startAutoSync() }
    }
    single<QuizReminderScheduler> { createQuizReminderScheduler() }
    single { SentenceRepository(userDataRepository = get(), lessonContentRepository = get()) }

    // Use Cases
    single { CheckDailyBonusUseCase(userDataRepository = get()) }
    single { CheckStreakRestorationBackfillUseCase(userDataRepository = get()) }
    single { RemoveHeartUseCase(userDataRepository = get()) }
    single { ScheduleQuizReminderUseCase(userDataRepository = get(), reminderScheduler = get()) }

    viewModel {
        AppViewModel(
            userDataRepository = get(),
            databaseRepository = get(),
            checkDailyBonusUseCase = get(),
            checkStreakRestorationBackfillUseCase = get(),
            scheduleQuizReminderUseCase = get()
        )
    }
    viewModel { IntroViewModel(userDataRepository = get(), analyticsRepository = get()) }
    viewModel {
        LandingViewModel(
            chapterRepository = get(),
            userDataRepository = get(),
            databaseRepository = get(),
            authRepository = get(),
            analyticsRepository = get()
        )
    }
    viewModel {
        ProfileViewModel(
            userDataRepository = get(),
            authRepository = get(),
            cloudSyncRepository = get(),
            analyticsRepository = get(),
            scheduleQuizReminderUseCase = get()
        )
    }
    viewModel { AuthViewModel(authRepository = get(), analyticsRepository = get()) }
    viewModel { params ->
        QuizViewModel(
            savedStateHandle = params.get(),
            lessonContentRepository = get(),
            userDataRepository = get(),
            removeHeartUseCase = get(),
            databaseRepository = get(),
            analyticsRepository = get(),
            scheduleQuizReminderUseCase = get(),
            sentenceRepository = get()
        )
    }
    viewModel { params ->
        ExplanationViewModel(
            savedStateHandle = params.get(),
            lessonContentRepository = get(),
            userDataRepository = get(),
            databaseRepository = get(),
            analyticsRepository = get()
        )
    }
    viewModel { params ->
        RuleDetailViewModel(
            savedStateHandle = params.get(),
            userDataRepository = get()
        )
    }
    viewModel { FeaturePollViewModel(userDataRepository = get(), analyticsRepository = get()) }
}
